<!--
  Shown the first time a structure is loaded (composables/betaNotice.ts). Continue records the acknowledgement and lets
  the load proceed; Cancel or closing the dialog cancels that load and the notice is shown again next time.
-->
<script setup lang="ts">
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { FULL_NANOME_URL, REPORT_BUG_URL, TERMS_URL, useBetaNotice } from '@/composables/betaNotice';

const { visible, accept, cancel } = useBetaNotice();
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :draggable="false"
    header="Nanome Lite (Beta)"
    class="w-[30rem] max-w-[calc(100vw-2rem)]"
    data-test="beta-notice"
    @update:visible="(v: boolean) => { if (!v) cancel() }"
  >
    <ul class="m-0 p-0 list-none flex flex-col gap-3 text-sm leading-snug">
      <li class="flex gap-3">
        <div class="i-tabler-cube-3d-sphere mt-0.5 shrink-0 text-primary" />
        <span>
          I understand this is Nanome Lite (Beta). The full Nanome, with multi-user workspaces, scenes and tools, is at
          <a :href="FULL_NANOME_URL" target="_blank" rel="noopener" class="text-primary underline">app.nanome.ai</a>.
        </span>
      </li>
      <li class="flex gap-3">
        <div class="i-tabler-bug mt-0.5 shrink-0 text-primary" />
        <span class="flex flex-col items-start gap-2">
          I understand this is a beta and there may be bugs.
          <Button
            as="a"
            :href="REPORT_BUG_URL"
            icon="i-tabler-bug"
            label="Report a bug"
            size="small"
            severity="secondary"
            rounded
            data-test="beta-report-bug"
          />
        </span>
      </li>
      <li class="flex gap-3">
        <div class="i-tabler-file-text mt-0.5 shrink-0 text-primary" />
        <span>
          By using Nanome Lite I agree to the terms at
          <a :href="TERMS_URL" target="_blank" rel="noopener" class="text-primary underline">nanome.ai/terms</a>.
        </span>
      </li>
    </ul>
    <template #footer>
      <Button label="Cancel" severity="secondary" text rounded data-test="beta-cancel" @click="cancel" />
      <Button label="I understand, continue" rounded autofocus data-test="beta-accept" @click="accept" />
    </template>
  </Dialog>
</template>
